import { useState } from "react";
import { CheckCircle2, PenLine, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, fmtTime, fmtVal } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { SourceBadge } from "@/components/meridian/SourceBadge";

function OverrideDialog({ a, onClose, onSave, actor }) {
  const pct = a?.format === "pct";
  const [val, setVal] = useState("");
  const [reason, setReason] = useState("");
  if (!a) return null;
  const submit = () => {
    const n = parseFloat(val);
    if (Number.isNaN(n) || !reason.trim()) return toast.error("Enter a value and a reason");
    onSave(a.key, pct ? n / 100 : n, reason);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="override-dialog" className="bg-white border-slate-300 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Override {a.label}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Sourced value {fmtVal(a.original_value ?? a.value, a.format)}. The override, your reason and your name are written to the audit trail, and every calculation is re-run.</DialogDescription>
        </DialogHeader>
        <label className="text-xs text-slate-600">New value {pct ? "(%)" : ""}</label>
        <input data-testid="override-value-input" value={val} onChange={(e) => setVal(e.target.value)} placeholder={pct ? "e.g. 7.5" : "e.g. 600000"} className="w-full bg-slate-50 border border-slate-300 rounded px-3 py-2 text-sm num outline-none focus:border-sky-500/60" />
        <label className="text-xs text-slate-600">Reason</label>
        <textarea data-testid="override-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Forward SOFR curve implies higher refinance rate" className="w-full bg-slate-50 border border-slate-300 rounded px-3 py-2 text-sm outline-none focus:border-sky-500/60" />
        <div className="text-[11px] text-slate-500">Recorded as: {actor}</div>
        <button data-testid="override-save-button" onClick={submit} className="bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium px-4 py-2 rounded transition-colors">Apply override</button>
      </DialogContent>
    </Dialog>
  );
}

export function Assumptions({ task, reload }) {
  const { personaLabel: actor } = useApp();
  const [editing, setEditing] = useState(null);
  if (!task.assumptions.length) return <div className="p-6 text-sm text-slate-500">Extract facts first to see governed assumptions.</div>;
  const act = async (fn, msg) => { try { await fn(); await reload(); toast.success(msg); } catch (e) { if (e.response?.status !== 403) toast.error("Action failed"); } };
  const save = (key, value, reason) => act(async () => { await api.put(`/tasks/${task.id}/assumptions/${key}/override`, { value, reason, by: actor }); setEditing(null); }, "Override applied and calculations re-run");
  return (
    <div className="p-6 space-y-4" data-testid="assumptions-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="eyebrow">Governed economic assumptions</div>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">For each assumption OmniFi keeps the original source document, its effective and retrieval dates, who supplied, reviewed or overrode it, and how sensitive the recommendation is to it.</p>
        </div>
        <label className="text-[11px] text-slate-500 flex items-center gap-2">Acting as
          <span data-testid="actor-input" className="text-xs text-slate-900 font-medium">{actor}</span>
        </label>
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-slate-500">{["Assumption", "Value used", "Source", "Effective", "Retrieved", "Supplied by", "Review / override", ""].map((h) => <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {task.assumptions.map((a) => (
              <tr key={a.key} className="border-t border-slate-200 align-top" data-testid={`assumption-row-${a.key}`}>
                <td className="px-3 py-2.5 text-slate-800">{a.label}</td>
                <td className="px-3 py-2.5 num whitespace-nowrap">
                  <span className={a.override ? "text-amber-700" : "text-slate-900"}>{fmtVal(a.value, a.format)}</span>
                  {a.override && <div className="text-[10px] text-slate-500 line-through">{fmtVal(a.original_value, a.format)}</div>}
                </td>
                <td className="px-3 py-2.5"><SourceBadge src={a.source} /></td>
                <td className="px-3 py-2.5 num text-slate-600 whitespace-nowrap">{a.effective_date || "-"}</td>
                <td className="px-3 py-2.5 num text-slate-500 whitespace-nowrap">{a.retrieved_at ? fmtTime(a.retrieved_at) : "-"}</td>
                <td className="px-3 py-2.5 text-[11px] text-slate-600">{a.supplied_by}</td>
                <td className="px-3 py-2.5 text-[11px] max-w-[220px]">
                  {a.review ? <div className="text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />{a.review.by} · {fmtTime(a.review.at)}</div> : <div className="text-slate-500">Not reviewed</div>}
                  {a.override && <div className="text-amber-700 mt-1">Override by {a.override.by}: "{a.override.reason}"</div>}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1 justify-end">
                    {!a.review && <button data-testid={`assumption-review-${a.key}`} onClick={() => act(() => api.post(`/tasks/${task.id}/assumptions/${a.key}/review`, { by: actor }), "Marked reviewed")} className="text-[10px] px-2 py-1 rounded border border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 whitespace-nowrap">Mark reviewed</button>}
                    <button data-testid={`assumption-override-${a.key}`} onClick={() => setEditing(a)} className="p-1 rounded border border-slate-300 text-slate-700 hover:border-sky-500/50" title="Override"><PenLine className="w-3 h-3" /></button>
                    {a.override && <button data-testid={`assumption-restore-${a.key}`} onClick={() => act(() => api.delete(`/tasks/${task.id}/assumptions/${a.key}/override`), "Restored sourced value")} className="p-1 rounded border border-slate-300 text-slate-700 hover:border-sky-500/50" title="Restore"><Undo2 className="w-3 h-3" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {task.recommendation?.sensitivity && (
        <div className="panel p-4">
          <div className="eyebrow mb-2">Sensitivity: how far each driver can move before the outcome changes</div>
          <div className="grid md:grid-cols-2 gap-2">
            {task.recommendation.sensitivity.map((s) => <div key={s.driver} className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid={`sensitivity-${s.driver.toLowerCase().replace(/\s+/g, "-")}`}>{s.text}</div>)}
          </div>
        </div>
      )}
      <OverrideDialog a={editing} actor={actor} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}
